import type { DateRange } from '@daypicker/react';
import { CalendarRange, ChevronDown, ChevronLeft, ChevronRight, FileText, ListFilter, Search, X, type LucideIcon } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';
import { FilterButton } from '@/components/knowledge-graph/chronology-panel/FilterControls';
import {
  TOOLBAR_BUTTON_ACTIVE_CLASSNAME,
  TOOLBAR_BUTTON_CLASSNAME,
} from '@/components/knowledge-graph/chronology-panel/toolbar-styles';
import {
  DATE_FILTER_CONTENT_CLASSNAME,
  DateRangeFilterEditor,
  formatDateRangeSummary,
} from '@/components/knowledge-graph/DateRangeFilterEditor';
import { OutputDisclaimer } from '@/components/shared/OutputDisclaimer';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  FilterCountBadge,
  type FilterFieldOption,
  FilterSelectionList,
  summarizeSelection,
} from '@/components/ui/data-table-filter-toolbar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { ChronologyEventLight } from '@/hooks/useChronology';
import type { CanonicalEntity, EntityRole } from '@/hooks/useEntities';
import {
  buildUniqueEntityNameIndex,
  normalizeEntityNameKey,
} from '@/lib/entity-name-match';
import { displayRoles, hasPrincipalRole, roleLabel, sideLabel } from '@/lib/role-labels';
import { cn } from '@/lib/utils';
import type { RegionalDateLocale } from '@/utils/date-utils';

// ── Role grouping ──────────────────────────────────────────────────

interface RoleGroup {
  key: string;
  label: string;
  roleTypes: Set<string>;
}

const ROLE_GROUPS: RoleGroup[] = [
  {
    key: 'parties',
    label: 'Parties & Counsel',
    roleTypes: new Set(['party']),
  },
  {
    key: 'amici',
    label: 'Amici',
    roleTypes: new Set(['amicus']),
  },
  {
    key: 'witnesses',
    label: 'Witnesses',
    roleTypes: new Set(['witness']),
  },
  {
    key: 'decision_makers',
    label: 'Decision Makers',
    roleTypes: new Set(['adjudicator', 'judge', 'arbitrator']),
  },
  {
    key: 'experts',
    label: 'Experts',
    roleTypes: new Set(['expert']),
  },
];

const CARD_GRID_CLASSNAME = 'grid grid-cols-1 gap-3.5 @min-[40rem]:grid-cols-2 @min-[64rem]:grid-cols-4';

function sideDotClass(sideAlignment: string | undefined): string {
  if (sideAlignment === 'claimant_side') return 'bg-sky-500/70';
  if (sideAlignment === 'respondent_side') return 'bg-rose-500/70';
  return 'bg-gray-300';
}

function entityKindLabel(entity: CanonicalEntity, isUs: boolean): string {
  if (entity.entity_kind === 'court_or_tribunal') return 'Court or tribunal';
  if (entity.entity_kind === 'person' || entity.entity_type === 'person') return 'Person';
  if (entity.entity_kind === 'organization' || entity.entity_type === 'organization') {
    return isUs ? 'Organization' : 'Organisation';
  }
  return 'Other';
}

function relationshipTargets(
  entity: CanonicalEntity,
  relationshipType: string,
): { id: string; name: string }[] {
  const targets: { id: string; name: string }[] = [];
  const seen = new Set<string>();
  for (const rel of entity.relationships) {
    if (rel.relationship_type !== relationshipType) continue;
    const name = rel.target_entity_name.trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    targets.push({ id: rel.target_entity_id, name });
  }
  return targets;
}

function pickRelatedName(
  candidates: { name: string; entity?: CanonicalEntity }[],
): string | null {
  if (candidates.length === 0) return null;
  const organization = candidates.find(candidate => candidate.entity && !isPersonEntity(candidate.entity));
  return (organization ?? candidates[0]).name;
}

// The counsel chain is lawyer -member_of-> firm -represents-> company.
// Show the nearest link: who this entity works for, else who they represent,
// else who represents them.
function isPersonEntity(entity: CanonicalEntity): boolean {
  return entity.entity_kind === 'person' || entity.entity_type === 'person';
}

// Firms stay "Legal Representative". The person is Counsel in the UK and Attorney in the US.
function representativeRoleLabel(entity: CanonicalEntity, roleType: string | undefined, isUs: boolean): string {
  if (roleType !== 'legal_representative') {
    return roleType ? roleLabel(roleType) : entityKindLabel(entity, isUs);
  }
  if (!isPersonEntity(entity)) return 'Legal Representative';
  return isUs ? 'Attorney' : 'Counsel';
}

function sameEntityName(left: string, right: string): boolean {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

function cardPositionText(
  roleText: string,
  roleType: string | undefined,
  redundantRoleTypes: readonly string[],
  relatedName: string | null,
  entityName: string,
): string | null {
  const related = relatedName && !sameEntityName(relatedName, entityName) ? relatedName : null;
  const roleIsRedundant = (roleType != null && redundantRoleTypes.includes(roleType))
    || (roleText === 'Other' && redundantRoleTypes.includes('other'));
  if (roleIsRedundant) return related;
  if (!roleText) return related;
  return related ? `${roleText} - ${related}` : roleText;
}

function relatedEntityName(
  entity: CanonicalEntity,
  representedBy: readonly string[],
  entityById: ReadonlyMap<string, CanonicalEntity>,
): string | null {
  const memberOf = relationshipTargets(entity, 'member_of');
  const represents = relationshipTargets(entity, 'represents');
  const outgoing = memberOf.length > 0 ? memberOf : represents;
  if (outgoing.length > 0) {
    return pickRelatedName(outgoing.map(target => ({
      name: target.name,
      entity: entityById.get(target.id),
    })));
  }

  const incoming: { name: string; entity?: CanonicalEntity }[] = [];
  const seen = new Set<string>();
  for (const name of representedBy) {
    const trimmed = name.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    incoming.push({
      name: trimmed,
      entity: Array.from(entityById.values()).find(candidate => candidate.name === trimmed),
    });
  }
  return pickRelatedName(incoming);
}

function dateRangeHasValue(dateRange: DateRange | undefined): boolean {
  return Boolean(dateRange?.from || dateRange?.to);
}

function dateInRange(date: Date, dateRange: DateRange | undefined): boolean {
  if (!dateRangeHasValue(dateRange)) return true;
  const from = dateRange?.from ? new Date(dateRange.from) : null;
  const to = dateRange?.to ? new Date(dateRange.to) : null;
  if (from) from.setHours(0, 0, 0, 0);
  if (to) to.setHours(23, 59, 59, 999);
  const time = date.getTime();
  if (from && time < from.getTime()) return false;
  if (to && time > to.getTime()) return false;
  return true;
}

function hasDisplayLegalRepresentativeRole(entity: CanonicalEntity): boolean {
  return entity.roles.some(role => role.role_type === 'legal_representative') && !hasPrincipalRole(entity.roles);
}

function getEntityGroups(entity: CanonicalEntity): string[] {
  const groups: string[] = [];
  for (const group of ROLE_GROUPS) {
    if (entity.roles.some(r => group.roleTypes.has(r.role_type))) groups.push(group.key);
  }
  return groups;
}

function getRoleForGroup(entity: CanonicalEntity, groupKey: string): EntityRole | undefined {
  const group = ROLE_GROUPS.find(g => g.key === groupKey);
  if (!group) return displayRoles(entity.roles)[0];
  return entity.roles.find(r => group.roleTypes.has(r.role_type))
    ?? ((groupKey === 'parties' || groupKey === 'amici') && !hasPrincipalRole(entity.roles)
      ? entity.roles.find(r => r.role_type === 'legal_representative')
      : undefined)
    ?? displayRoles(entity.roles)[0];
}

function getLegalRepresentativeGroupKeys(
  entity: CanonicalEntity,
  entityById: ReadonlyMap<string, CanonicalEntity>,
): string[] {
  if (!hasDisplayLegalRepresentativeRole(entity)) return [];

  const representedEntityIds = new Set<string>();
  for (const rel of entity.relationships) {
    if (rel.relationship_type === 'represents') {
      representedEntityIds.add(rel.target_entity_id);
    }
    if (rel.relationship_type !== 'member_of') continue;
    const parent = entityById.get(rel.target_entity_id);
    if (!parent) continue;
    for (const parentRel of parent.relationships) {
      if (parentRel.relationship_type === 'represents') {
        representedEntityIds.add(parentRel.target_entity_id);
      }
    }
  }

  const groupKeys = new Set<string>();
  for (const targetId of representedEntityIds) {
    const target = entityById.get(targetId);
    if (!target) continue;
    if (target.roles.some(role => role.role_type === 'party')) groupKeys.add('parties');
    if (target.roles.some(role => role.role_type === 'amicus')) groupKeys.add('amici');
  }

  if (groupKeys.size === 0) {
    groupKeys.add('parties');
  }

  return Array.from(groupKeys);
}

function getEntityDisplayGroupKeys(
  entity: CanonicalEntity,
  entityById: ReadonlyMap<string, CanonicalEntity>,
): Set<string> {
  const groupKeys = new Set<string>(getEntityGroups(entity));
  for (const key of getLegalRepresentativeGroupKeys(entity, entityById)) {
    groupKeys.add(key);
  }
  if (groupKeys.size === 0) {
    groupKeys.add('other');
  }
  return groupKeys;
}

export const ENTITY_CATEGORIES = [
  ...ROLE_GROUPS.map(({ key, label }) => ({ key, label })),
  { key: 'other', label: 'Other' },
] as const;

export type EntityCategoryKey = (typeof ENTITY_CATEGORIES)[number]['key'];
export type EntityCardDetail = 'documents' | 'events' | 'mentions';

// The card already sits under a heading that names this role, so repeating
// "Party", "Counsel", "Witness", and so on on the card is redundant.
const REDUNDANT_ROLE_TYPES_BY_CATEGORY: Partial<Record<EntityCategoryKey, readonly string[]>> = {
  witnesses: ['witness'],
  experts: ['expert'],
  amici: ['amicus'],
  other: ['other'],
};
type PartySideSection = 'claimant' | 'respondent' | 'other';

export function entityCategoryKeys(
  entity: CanonicalEntity,
  entityById: ReadonlyMap<string, CanonicalEntity>,
): EntityCategoryKey[] {
  return Array.from(getEntityDisplayGroupKeys(entity, entityById)) as EntityCategoryKey[];
}

export function entityCategoryCounts(
  entities: readonly CanonicalEntity[],
): Map<EntityCategoryKey, number> {
  const counts = new Map<EntityCategoryKey, number>();
  for (const category of ENTITY_CATEGORIES) counts.set(category.key, 0);
  const entityById = new Map(entities.map(entity => [entity.id, entity]));
  for (const entity of entities) {
    for (const key of entityCategoryKeys(entity, entityById)) {
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  return counts;
}

// Claimant wins when an entity is aligned to both sides. No alignment stays in Other.
function partySideSection(entity: CanonicalEntity): PartySideSection {
  let claimant = false;
  let respondent = false;
  for (const role of entity.roles) {
    if (role.side_alignment === 'claimant_side') claimant = true;
    else if (role.side_alignment === 'respondent_side') respondent = true;
  }
  if (claimant) return 'claimant';
  if (respondent) return 'respondent';
  return 'other';
}

// Firms first, each followed by the lawyers who belong to it. Lawyers with no
// firm in this group stay at the end, and a lawyer is listed only once.
function orderCounsel(entities: CanonicalEntity[]): CanonicalEntity[] {
  const firms = entities.filter(entity => !isPersonEntity(entity));
  const people = entities.filter(entity => isPersonEntity(entity));
  const placed = new Set<string>();
  const ordered: CanonicalEntity[] = [];

  for (const firm of firms) {
    ordered.push(firm);
    placed.add(firm.id);
    for (const person of people) {
      if (placed.has(person.id)) continue;
      const belongsToFirm = person.relationships.some(
        rel => rel.relationship_type === 'member_of' && rel.target_entity_id === firm.id,
      );
      if (!belongsToFirm) continue;
      ordered.push(person);
      placed.add(person.id);
    }
  }

  for (const person of people) {
    if (!placed.has(person.id)) ordered.push(person);
  }
  return ordered;
}

function splitSideEntities(entities: CanonicalEntity[]): { parties: CanonicalEntity[]; counsel: CanonicalEntity[] } {
  const parties: CanonicalEntity[] = [];
  const counsel: CanonicalEntity[] = [];
  for (const entity of entities) {
    if (entity.roles.some(role => role.role_type === 'party')) parties.push(entity);
    else counsel.push(entity);
  }
  return { parties, counsel: orderCounsel(counsel) };
}

function EntityCategoryBody({
  category,
  entities,
  hasActiveFilters,
  entityById,
  selectedId,
  detailView,
  eventCountByEntityId,
  representedByName,
  onSelect,
  onOpenDetail,
}: {
  category: EntityCategoryKey;
  entities: CanonicalEntity[];
  hasActiveFilters: boolean;
  entityById: ReadonlyMap<string, CanonicalEntity>;
  selectedId: string | null;
  detailView: EntityCardDetail | null;
  eventCountByEntityId: ReadonlyMap<string, number>;
  representedByName: ReadonlyMap<string, string[]>;
  onSelect: (entity: CanonicalEntity) => void;
  onOpenDetail: (entity: CanonicalEntity, view: EntityCardDetail) => void;
}) {
  const { jurisdiction } = useMatterJurisdiction();

  if (entities.length === 0) {
    return (
      <div className="px-6 py-12 text-center">
        <p className="text-sm text-slate-500">
          {hasActiveFilters ? 'No entities match these filters.' : 'No entities here.'}
        </p>
      </div>
    );
  }

  const gridProps = {
    selectedId,
    detailView,
    eventCountByEntityId,
    representedByName,
    entityById,
    onSelect,
    onOpenDetail,
  };

  if (category !== 'parties') {
    return (
      <EntityCardGrid
        entities={entities}
        groupKey={category}
        redundantRoleTypes={REDUNDANT_ROLE_TYPES_BY_CATEGORY[category] ?? []}
        sideImpliedBySection={false}
        {...gridProps}
      />
    );
  }

  const partySections: { key: PartySideSection; label: string }[] = [
    { key: 'claimant', label: jurisdiction.ui_labels.claimant_side_plural },
    { key: 'respondent', label: jurisdiction.ui_labels.respondent_side_plural },
    { key: 'other', label: 'Other' },
  ];

  return (
    <>
      {partySections.flatMap(section => {
        const sectionEntities = entities.filter(entity => partySideSection(entity) === section.key);
        if (sectionEntities.length === 0) return [];
        const { parties, counsel } = splitSideEntities(sectionEntities);
        const groups = [
          { key: 'parties', label: 'Parties', entities: parties },
          { key: 'counsel', label: 'Counsel', entities: counsel },
        ].filter(group => group.entities.length > 0);
        return [(
          <section key={section.key} className="space-y-3">
            <h2 className="text-sm font-medium text-gray-600">
              {section.label}
              <span className="ml-2 text-xs font-normal text-gray-400">{sectionEntities.length}</span>
            </h2>
            {groups.map(group => (
              <div key={group.key}>
                <h3 className="mb-2 pt-1.5 text-xs font-medium text-gray-500">
                  {group.label}
                  <span className="ml-2 font-normal text-gray-400">{group.entities.length}</span>
                </h3>
                <EntityCardGrid
                  entities={group.entities}
                  groupKey="parties"
                  redundantRoleTypes={group.key === 'counsel' ? ['legal_representative'] : ['party']}
                  sideImpliedBySection={section.key !== 'other'}
                  {...gridProps}
                />
              </div>
            ))}
          </section>
        )];
      })}
    </>
  );
}

// ── Components ─────────────────────────────────────────────────────

interface EntitiesPanelProps {
  entities: CanonicalEntity[];
  events?: ChronologyEventLight[];
  sourceNameByDocumentGuid?: ReadonlyMap<string, string>;
  category?: EntityCategoryKey;
  onOpenSidePanel?: (entity: CanonicalEntity, view: EntityCardDetail | null) => void;
  onCloseSidePanel?: () => void;
  panelOpen?: boolean;
}

interface EntityEventFacets {
  sourceNames: Set<string>;
  dates: Date[];
  eventCount: number;
}

function EntityPosition({
  side,
  text,
  className,
}: { side: string | undefined; text: string; className?: string }) {
  const { jurisdiction } = useMatterJurisdiction();
  const sideName = side === 'claimant_side' || side === 'respondent_side'
    ? sideLabel(side, jurisdiction.ui_labels)
    : '';
  return (
    <span className={cn('flex items-center gap-1.5 text-xs font-medium text-gray-500', className)}>
      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', sideDotClass(side))} aria-hidden="true" />
      {text ? <span className="truncate">{text}</span> : <span className="sr-only">{sideName}</span>}
    </span>
  );
}

const ENTITY_CARD_CLASSNAME = cn(
  'group relative flex w-full flex-col overflow-hidden rounded-2xl bg-white text-left',
  'border border-gray-200 shadow-md transition-all duration-200',
  'hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-lg',
  // Keep the card itself flat while a stat is hovered, so only the stat highlights.
  'has-[.stat-hit:hover]:translate-y-0 has-[.stat-hit:hover]:border-gray-200 has-[.stat-hit:hover]:shadow-md',
);

function StatLink({
  value,
  label,
  pressed = false,
  onClick,
}: {
  value: ReactNode;
  label: string;
  pressed?: boolean;
  onClick?: () => void;
}) {
  const className = cn(
    'stat-hit group/stat flex flex-col rounded-md px-2 py-1 text-left transition-colors',
    onClick && 'cursor-pointer hover:bg-gray-100',
    pressed && 'bg-gray-100',
  );
  const content = (
    <>
      <span className="text-lg font-semibold tabular-nums text-gray-900">{value}</span>
      <span className={cn(
        'text-[10px] font-medium uppercase tracking-wide text-gray-400',
        onClick && 'transition-colors group-hover/stat:text-gray-700',
      )}>
        {label}
      </span>
    </>
  );
  if (!onClick) return <span className={className}>{content}</span>;
  return (
    <button type="button" onClick={onClick} aria-pressed={pressed} className={className}>
      {content}
    </button>
  );
}

function EntityInfoCard({
  entity,
  role,
  isSelected,
  detailView,
  onSelect,
  onOpenDetail,
  eventCount,
  representedBy = [],
  entityById,
  redundantRoleTypes,
  sideImpliedBySection,
}: {
  entity: CanonicalEntity;
  role?: EntityRole;
  isSelected: boolean;
  detailView: EntityCardDetail | null;
  onSelect: () => void;
  onOpenDetail: (view: EntityCardDetail) => void;
  eventCount: number;
  representedBy?: readonly string[];
  entityById: ReadonlyMap<string, CanonicalEntity>;
  redundantRoleTypes: readonly string[];
  sideImpliedBySection: boolean;
}) {
  const { locale } = useMatterJurisdiction();
  const position = role ?? displayRoles(entity.roles)[0];
  const side = position?.side_alignment;
  const roleText = representativeRoleLabel(entity, position?.role_type, locale === 'us');
  const relatedName = relatedEntityName(entity, representedBy, entityById);
  const positionText = cardPositionText(
    roleText,
    position?.role_type,
    redundantRoleTypes,
    relatedName,
    entity.name,
  );
  const showSideOnly = !positionText
    && !sideImpliedBySection
    && (side === 'claimant_side' || side === 'respondent_side');
  const showPosition = positionText !== null || showSideOnly;
  const description = entity.description?.trim() || position?.role_description?.trim() || '';
  const alias = entity.aliases.find(value => {
    const trimmed = value.trim();
    return trimmed.length > 0 && trimmed.toLowerCase() !== entity.name.trim().toLowerCase();
  });
  const bodyText = description || (alias ? `Also known as ${alias.trim()}` : '');
  const activeView = isSelected ? detailView : null;

  return (
    <div
      data-entity-id={entity.id}
      className={cn(
        ENTITY_CARD_CLASSNAME,
        'h-full rounded-xl px-5 py-4',
        isSelected && 'border-gray-400 ring-1 ring-gray-300 hover:border-gray-400 has-[.stat-hit:hover]:border-gray-400',
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={isSelected}
        aria-label={entity.name}
        className="absolute inset-0 rounded-xl focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-400"
      />
      <div className="pointer-events-none relative z-10 flex h-full flex-col">
        <span className="block truncate text-base font-semibold text-gray-900">{entity.name}</span>
        {showPosition ? (
          <EntityPosition side={side} text={positionText ?? ''} className="mt-0.5 text-xs" />
        ) : null}
        {bodyText ? (
          <span className="mt-2 line-clamp-3 min-h-0 text-[13px] leading-5 text-gray-600">{bodyText}</span>
        ) : null}
        <div className="pointer-events-auto -ml-2 mt-auto flex items-center gap-3 pt-3">
          <StatLink
            value={entity.document_guids.length}
            label="Documents"
            pressed={activeView === 'documents'}
            onClick={() => onOpenDetail('documents')}
          />
          <StatLink
            value={entity.mention_count ?? 0}
            label="Mentions"
            pressed={activeView === 'mentions'}
            onClick={() => onOpenDetail('mentions')}
          />
          <StatLink
            value={eventCount}
            label="Events"
            pressed={activeView === 'events'}
            onClick={() => onOpenDetail('events')}
          />
        </div>
      </div>
    </div>
  );
}

function EntityCardGrid({
  entities,
  groupKey,
  redundantRoleTypes,
  sideImpliedBySection,
  selectedId,
  detailView,
  eventCountByEntityId,
  representedByName,
  entityById,
  onSelect,
  onOpenDetail,
}: {
  entities: CanonicalEntity[];
  groupKey: string;
  redundantRoleTypes: readonly string[];
  sideImpliedBySection: boolean;
  selectedId: string | null;
  detailView: EntityCardDetail | null;
  eventCountByEntityId: ReadonlyMap<string, number>;
  representedByName: ReadonlyMap<string, readonly string[]>;
  entityById: ReadonlyMap<string, CanonicalEntity>;
  onSelect: (entity: CanonicalEntity) => void;
  onOpenDetail: (entity: CanonicalEntity, view: EntityCardDetail) => void;
}) {
  return (
    <div className={CARD_GRID_CLASSNAME}>
      {entities.map(entity => (
        <EntityInfoCard
          key={entity.id}
          entity={entity}
          role={getRoleForGroup(entity, groupKey)}
          isSelected={selectedId === entity.id}
          detailView={detailView}
          eventCount={eventCountByEntityId.get(entity.id) ?? 0}
          representedBy={representedByName.get(entity.id) ?? []}
          entityById={entityById}
          redundantRoleTypes={redundantRoleTypes}
          sideImpliedBySection={sideImpliedBySection}
          onSelect={() => onSelect(entity)}
          onOpenDetail={view => onOpenDetail(entity, view)}
        />
      ))}
    </div>
  );
}


function EntityActiveFilter({
  icon,
  label,
  value,
  onClear,
  contentClassName,
  children,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  onClear: () => void;
  contentClassName?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <FilterButton
      icon={icon}
      label={label}
      value={value}
      onClear={onClear}
      open={open}
      onOpenChange={setOpen}
      contentClassName={contentClassName}
    >
      {children}
    </FilterButton>
  );
}

function EntityFiltersMenu({
  fields,
}: {
  fields: {
    id: string;
    label: string;
    count: number;
    contentClassName?: string;
    render: () => ReactNode;
  }[];
}) {
  const [open, setOpen] = useState(false);
  const [drillFieldId, setDrillFieldId] = useState<string | null>(null);
  const drillField = fields.find(field => field.id === drillFieldId) ?? null;
  const totalCount = fields.reduce((sum, field) => sum + field.count, 0);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setDrillFieldId(null);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            TOOLBAR_BUTTON_CLASSNAME,
            'relative overflow-visible',
            (open || totalCount > 0) && TOOLBAR_BUTTON_ACTIVE_CLASSNAME,
          )}
          aria-label="Filters"
        >
          <ListFilter className={cn('h-3.5 w-3.5 shrink-0', totalCount > 0 || open ? 'text-gray-600' : 'text-gray-400')} />
          <span>Filters</span>
          <ChevronDown className={cn('h-3 w-3 shrink-0 text-gray-400 transition-transform', open && 'rotate-180')} />
          <FilterCountBadge count={totalCount} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={drillField ? (drillField.contentClassName ?? 'w-[260px] p-0') : 'w-[260px] p-0'}
      >
        {drillField ? (
          <div>
            <button
              type="button"
              className="flex w-full items-center gap-1 border-b border-gray-100 px-2 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={() => setDrillFieldId(null)}
            >
              <ChevronLeft className="h-4 w-4 text-gray-400" />
              {drillField.label}
            </button>
            {drillField.render()}
          </div>
        ) : (
          <Command>
            <CommandList>
              <CommandGroup heading="Filter by">
                {fields.map((field) => (
                  <CommandItem
                    key={field.id}
                    onSelect={() => setDrillFieldId(field.id)}
                    className="flex items-center"
                  >
                    <span>{field.label}</span>
                    <span className="ml-auto flex items-center gap-1 text-gray-400">
                      {field.count > 0 && (
                        <span className="rounded bg-gray-200 px-1.5 text-xs font-medium text-gray-700">
                          {field.count}
                        </span>
                      )}
                      <ChevronRight className="h-4 w-4" />
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}

function EntityFilterBar({
  searchQuery,
  onSearchChange,
  dateRange,
  onDateRangeChange,
  onClearDateRange,
  eventYears,
  locale,
  sourceOptions,
  sourceSummary,
  activeSources,
  onActiveSourcesChange,
  onClearAll,
}: {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  dateRange: DateRange | undefined;
  onDateRangeChange: (range: DateRange | undefined) => void;
  onClearDateRange: () => void;
  eventYears: number[];
  locale: RegionalDateLocale;
  sourceOptions: FilterFieldOption[];
  sourceSummary: string;
  activeSources: ReadonlySet<string>;
  onActiveSourcesChange: (next: Set<string>) => void;
  onClearAll: () => void;
}) {
  const dateActive = dateRangeHasValue(dateRange);
  const dateSummary = formatDateRangeSummary(dateRange, locale);
  const sourceActive = activeSources.size > 0;
  const showClearAll = searchQuery.trim().length > 0
    || sourceActive
    || dateActive;
  const menuFields = [
    {
      id: 'source-date',
      label: 'Source Date',
      count: dateActive ? 1 : 0,
      contentClassName: DATE_FILTER_CONTENT_CLASSNAME,
      render: () => (
        <DateRangeFilterEditor value={dateRange} onChange={onDateRangeChange} years={eventYears} />
      ),
    },
  ];
  if (sourceOptions.length > 0) {
    menuFields.push({
      id: 'source',
      label: 'Source',
      count: activeSources.size,
      contentClassName: 'w-[260px] p-0',
      render: () => (
        <FilterSelectionList
          label="Source"
          options={sourceOptions}
          selected={activeSources}
          onSelectedChange={onActiveSourcesChange}
        />
      ),
    });
  }

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex w-full min-w-0 flex-wrap items-center gap-2">
        <div className="relative min-w-[12rem] max-w-[22rem] flex-1 basis-60">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={event => onSearchChange(event.target.value)}
            placeholder="Search entities..."
            aria-label="Search entities"
            className={cn(
              'h-8 w-full rounded-md border bg-white pl-8 pr-8 text-[13px] text-gray-800 transition-colors placeholder:text-gray-400',
              'focus:border-gray-400 focus:outline-hidden focus:ring-2 focus:ring-gray-200/70',
              searchQuery ? 'border-gray-300' : 'border-gray-200 hover:border-gray-300',
            )}
          />
          {searchQuery.length > 0 && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-sm text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Clear search"
              title="Clear search"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        <EntityFiltersMenu fields={menuFields} />

        {showClearAll && (
          <button
            type="button"
            onClick={onClearAll}
            className="inline-flex h-8 shrink-0 items-center px-2 text-[13px] text-gray-500 hover:text-gray-900"
          >
            Clear all
            <X className="ml-1 h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {(dateActive || sourceActive) && (
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {dateActive && (
            <EntityActiveFilter
              icon={CalendarRange}
              label="Source Date"
              value={dateSummary}
              onClear={onClearDateRange}
              contentClassName={DATE_FILTER_CONTENT_CLASSNAME}
            >
              <DateRangeFilterEditor value={dateRange} onChange={onDateRangeChange} years={eventYears} />
            </EntityActiveFilter>
          )}
          {sourceActive && (
            <EntityActiveFilter
              icon={FileText}
              label="Source"
              value={sourceSummary || `${activeSources.size} selected`}
              onClear={() => onActiveSourcesChange(new Set())}
              contentClassName="w-[260px]"
            >
              <FilterSelectionList
                label="Source"
                options={sourceOptions}
                selected={activeSources}
                onSelectedChange={onActiveSourcesChange}
              />
            </EntityActiveFilter>
          )}
        </div>
      )}
    </div>
  );
}

export function EntitiesPanel({
  entities,
  events = [],
  sourceNameByDocumentGuid,
  category = 'parties',
  onOpenSidePanel,
  onCloseSidePanel,
  panelOpen = false,
}: EntitiesPanelProps) {
  const { locale } = useMatterJurisdiction();
  const [expanded, setExpanded] = useState<{ id: string; view: EntityCardDetail | null } | null>(null);
  const [expandedCategory, setExpandedCategory] = useState(category);
  const [trackedPanelOpen, setTrackedPanelOpen] = useState(panelOpen);
  if (trackedPanelOpen !== panelOpen) {
    setTrackedPanelOpen(panelOpen);
    if (!panelOpen) setExpanded(null);
  }
  if (expandedCategory !== category) {
    setExpandedCategory(category);
    setExpanded(null);
  }
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSources, setActiveSources] = useState<Set<string>>(new Set());
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);

  const entityEventFacets = useMemo(() => {
    const nameIndex = buildUniqueEntityNameIndex(entities);
    const facets = new Map<string, EntityEventFacets>();

    for (const entity of entities) {
      const sourceNames = new Set<string>();
      for (const documentGuid of entity.document_guids) {
        const sourceName = sourceNameByDocumentGuid?.get(documentGuid)?.trim();
        if (sourceName) sourceNames.add(sourceName);
      }
      facets.set(entity.id, {
        sourceNames,
        dates: [],
        eventCount: 0,
      });
    }

    for (const event of events) {
      const entityIds = new Set<string>();
      for (const name of event.entity_names || []) {
        const id = nameIndex.get(normalizeEntityNameKey(name));
        if (id) entityIds.add(id);
      }
      if (entityIds.size === 0) continue;

      for (const id of entityIds) {
        const facet = facets.get(id);
        if (!facet) continue;
        facet.eventCount += 1;
        for (const sourceDate of event.source_document_dates || []) {
          facet.dates.push(sourceDate);
        }
      }
    }

    return facets;
  }, [entities, events, sourceNameByDocumentGuid]);

  const eventCountByEntityId = useMemo(() => {
    const counts = new Map<string, number>();
    for (const [id, facet] of entityEventFacets) counts.set(id, facet.eventCount);
    return counts;
  }, [entityEventFacets]);

  const entityById = useMemo(
    () => new Map(entities.map(entity => [entity.id, entity])),
    [entities],
  );

  const representedByName = useMemo(() => {
    const namesByTarget = new Map<string, string[]>();
    for (const entity of entities) {
      for (const rel of entity.relationships) {
        if (rel.relationship_type !== 'represents' || entity.id === rel.target_entity_id) continue;
        const names = namesByTarget.get(rel.target_entity_id) ?? [];
        if (!names.includes(entity.name)) names.push(entity.name);
        namesByTarget.set(rel.target_entity_id, names);
      }
    }
    return namesByTarget;
  }, [entities]);

  const eventYears = useMemo(() => {
    const years = new Set<number>();
    for (const facet of entityEventFacets.values()) {
      for (const date of facet.dates) years.add(date.getFullYear());
    }
    return Array.from(years).sort();
  }, [entityEventFacets]);

  const searchedEntities = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return entities;
    return entities.filter(ent => {
      if (ent.name.toLowerCase().includes(q)) return true;
      if (ent.aliases.some(a => a.toLowerCase().includes(q))) return true;
      if (ent.description?.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [entities, searchQuery]);

  const entityMatchesDateRange = useCallback((entity: CanonicalEntity): boolean => {
    if (!dateRangeHasValue(dateRange)) return true;
    const facet = entityEventFacets.get(entity.id);
    return !!facet?.dates.some(date => dateInRange(date, dateRange));
  }, [dateRange, entityEventFacets]);

  const entitiesBeforeSourceFilter = useMemo(
    () => searchedEntities.filter(entity => entityMatchesDateRange(entity)),
    [entityMatchesDateRange, searchedEntities],
  );

  const sourceCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const entity of entitiesBeforeSourceFilter) {
      const facet = entityEventFacets.get(entity.id);
      if (!facet) continue;
      for (const sourceName of facet.sourceNames) {
        counts.set(sourceName, (counts.get(sourceName) ?? 0) + 1);
      }
    }
    for (const sourceName of activeSources) {
      if (!counts.has(sourceName)) counts.set(sourceName, 0);
    }
    return counts;
  }, [activeSources, entityEventFacets, entitiesBeforeSourceFilter]);

  const entityMatchesSources = useCallback((entity: CanonicalEntity): boolean => {
    if (activeSources.size === 0) return true;
    const facet = entityEventFacets.get(entity.id);
    if (!facet) return false;
    for (const sourceName of activeSources) {
      if (facet.sourceNames.has(sourceName)) return true;
    }
    return false;
  }, [activeSources, entityEventFacets]);

  const filteredEntities = useMemo(
    () => entitiesBeforeSourceFilter.filter(entity => entityMatchesSources(entity)),
    [entityMatchesSources, entitiesBeforeSourceFilter],
  );

  const categoryCounts = useMemo(() => entityCategoryCounts(entities), [entities]);

  const groupedEntities = useMemo(() => {
    const groups = new Map<string, CanonicalEntity[]>();
    for (const entityCategory of ENTITY_CATEGORIES) groups.set(entityCategory.key, []);

    for (const entity of filteredEntities) {
      for (const groupKey of entityCategoryKeys(entity, entityById)) {
        groups.get(groupKey)?.push(entity);
      }
    }

    return groups;
  }, [entityById, filteredEntities]);

  const listRef = useRef<HTMLDivElement>(null);
  const cardAnchorRef = useRef<number | null>(null);
  const ignoreScrollUntilRef = useRef(0);

  const measureCardOffset = useCallback((id: string) => {
    const list = listRef.current;
    if (!list) return null;
    const card = list.querySelector(`[data-entity-id="${CSS.escape(id)}"]`);
    if (!(card instanceof HTMLElement)) return null;
    return card.getBoundingClientRect().top - list.getBoundingClientRect().top;
  }, []);

  const rememberCardAnchor = useCallback((id: string) => {
    const offset = measureCardOffset(id);
    if (offset != null) cardAnchorRef.current = offset;
  }, [measureCardOffset]);

  const captureCardAnchor = useCallback((id: string) => {
    rememberCardAnchor(id);
    ignoreScrollUntilRef.current = performance.now() + 150;
  }, [rememberCardAnchor]);

  const restoreCardAnchor = useCallback((id: string) => {
    const list = listRef.current;
    const target = cardAnchorRef.current;
    if (!list || target == null) return;
    const offset = measureCardOffset(id);
    if (offset == null) return;
    const delta = offset - target;
    if (Math.abs(delta) < 2) return;
    ignoreScrollUntilRef.current = performance.now() + 150;
    list.scrollTop += delta;
  }, [measureCardOffset]);

  const handleSelect = useCallback((entity: CanonicalEntity) => {
    const closing = expanded?.id === entity.id && expanded.view === null;
    if (!closing) captureCardAnchor(entity.id);
    setExpanded(closing ? null : { id: entity.id, view: null });
    if (closing) onCloseSidePanel?.();
    else onOpenSidePanel?.(entity, null);
  }, [captureCardAnchor, expanded, onCloseSidePanel, onOpenSidePanel]);

  const handleOpenDetail = useCallback((entity: CanonicalEntity, view: EntityCardDetail) => {
    const closing = expanded?.id === entity.id && expanded.view === view;
    if (!closing) captureCardAnchor(entity.id);
    setExpanded(closing ? { id: entity.id, view: null } : { id: entity.id, view });
    if (closing) onCloseSidePanel?.();
    else onOpenSidePanel?.(entity, view);
  }, [captureCardAnchor, expanded, onCloseSidePanel, onOpenSidePanel]);

  useLayoutEffect(() => {
    if (!expanded) return;
    restoreCardAnchor(expanded.id);
  }, [expanded, restoreCardAnchor]);

  useEffect(() => {
    const list = listRef.current;
    if (!list || !expanded) return;
    const selectedId = expanded.id;
    const onScroll = () => {
      if (performance.now() < ignoreScrollUntilRef.current) return;
      rememberCardAnchor(selectedId);
    };
    list.addEventListener('scroll', onScroll, { passive: true });
    const observer = new ResizeObserver(() => {
      restoreCardAnchor(selectedId);
    });
    observer.observe(list);
    return () => {
      list.removeEventListener('scroll', onScroll);
      observer.disconnect();
    };
  }, [expanded, rememberCardAnchor, restoreCardAnchor]);

  const clearDateRange = useCallback(() => {
    setDateRange(undefined);
  }, []);

  const clearAllFilters = useCallback(() => {
    setSearchQuery('');
    setActiveSources(new Set());
    setDateRange(undefined);
  }, []);

  const entityFilterOptions = useMemo(() => {
    const sourceOptions = Array.from(sourceCounts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([name]) => ({ label: name, value: name }));
    return {
      sourceOptions,
      sourceSummary: summarizeSelection(
        sourceOptions.filter(option => activeSources.has(option.value)).map(option => option.label),
      ),
    };
  }, [activeSources, sourceCounts]);

  const hasActiveFilters = searchQuery.trim().length > 0
    || activeSources.size > 0
    || dateRangeHasValue(dateRange);
  const tabEntityCount = categoryCounts.get(category) ?? 0;
  const visibleTabCount = groupedEntities.get(category)?.length ?? 0;
  const summaryLabel = visibleTabCount === tabEntityCount
    ? `Showing all ${tabEntityCount} ${tabEntityCount === 1 ? 'entity' : 'entities'}`
    : `Showing ${visibleTabCount} of ${tabEntityCount} ${tabEntityCount === 1 ? 'entity' : 'entities'}`;

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-50/80">
      {entities.length > 0 && (
        <div className="shrink-0 bg-slate-50/80 px-6 py-3">
          <EntityFilterBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            onClearDateRange={clearDateRange}
            eventYears={eventYears}
            locale={locale}
            sourceOptions={entityFilterOptions.sourceOptions}
            sourceSummary={entityFilterOptions.sourceSummary}
            activeSources={activeSources}
            onActiveSourcesChange={setActiveSources}
            onClearAll={clearAllFilters}
          />
        </div>
      )}
      <div ref={listRef} className="subtle-scrollbar min-h-0 flex-1 overflow-y-scroll">
        {entities.length > 0 ? (
          <>
            {hasActiveFilters && (
              <div className="px-6 pt-2">
                  <span className="text-[11px] text-gray-500">{summaryLabel}</span>
              </div>
            )}
            <div className="@container w-full px-6 py-4 space-y-6">
              <EntityCategoryBody
                category={category}
                entities={groupedEntities.get(category) || []}
                hasActiveFilters={hasActiveFilters}
                entityById={entityById}
                selectedId={panelOpen ? (expanded?.id ?? null) : null}
                detailView={panelOpen ? (expanded?.view ?? null) : null}
                eventCountByEntityId={eventCountByEntityId}
                representedByName={representedByName}
                onSelect={handleSelect}
                onOpenDetail={handleOpenDetail}
              />
            </div>
          </>
        ) : (
          <div className="px-6 py-4">
            <div className="text-center py-8 text-gray-400">
              <p className="text-sm">No entities extracted yet.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
